import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMerchItems, createMerchItem, updateMerchItem, deleteMerchItem, type MerchItem } from '../../services/api/merch';

export default function MerchManagerPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<MerchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState({ title_en: '', title_th: '', shopify_url: '', image_url: '' });

  useEffect(() => {
    getMerchItems()
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title_en.trim() || !form.shopify_url.trim()) return;
    await createMerchItem({ ...form, title_th: form.title_th || null, image_url: form.image_url || null, sort_order: items.length });
    setItems(await getMerchItems());
    setForm({ title_en: '', title_th: '', shopify_url: '', image_url: '' });
  }

  async function handleUpdate(id: string, payload: Partial<MerchItem>) {
    await updateMerchItem(id, payload);
    setItems(await getMerchItems());
    setEditing(null);
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this item?')) return;
    await deleteMerchItem(id);
    setItems(await getMerchItems());
  }

  if (loading) return <p className="text-[var(--color-text-muted)]">{t('common.loading')}</p>;

  return (
    <div className="admin-page">
      <h1 className="admin-page__title">{t('admin.merchTitle')}</h1>
      <form onSubmit={handleAdd} className="admin-form-row admin-form-row--grid">
        <div className="admin-form-group">
          <input placeholder="Title (EN)" value={form.title_en} onChange={(e) => setForm((f) => ({ ...f, title_en: e.target.value }))} />
        </div>
        <div className="admin-form-group">
          <input placeholder="Title (TH)" value={form.title_th} onChange={(e) => setForm((f) => ({ ...f, title_th: e.target.value }))} />
        </div>
        <div className="admin-form-group">
          <input placeholder="Shopify product URL" value={form.shopify_url} onChange={(e) => setForm((f) => ({ ...f, shopify_url: e.target.value }))} />
        </div>
        <div className="admin-form-group">
          <input placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} />
        </div>
        <button type="submit" className="admin-btn">{t('admin.addItem')}</button>
      </form>
      <div className="admin-merch-list">
        {items.map((item) => (
          <div key={item.id} className="admin-merch-item">
            {item.image_url && <img src={item.image_url} alt="" className="admin-merch-item__img" />}
            <div className="admin-merch-item__body">
              {editing === item.id ? (
                <input
                  defaultValue={item.title_en}
                  onBlur={(e) => handleUpdate(item.id, { title_en: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && setEditing(null)}
                />
              ) : (
                <p className="admin-merch-item__title">{item.title_en}</p>
              )}
              <a href={item.shopify_url} target="_blank" rel="noopener noreferrer" className="admin-merch-item__link">Link</a>
            </div>
            <button type="button" onClick={() => setEditing(editing === item.id ? null : item.id)} className="admin-btn admin-btn--sm">{t('common.edit')}</button>
            <button type="button" onClick={() => handleDelete(item.id)} className="admin-btn admin-btn--danger admin-btn--sm">{t('common.delete')}</button>
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="admin-page__empty">{t('admin.noMerch')}</p>}
    </div>
  );
}
